/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useIdleTimer } from 'react-idle-timer';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { regenerateSessionToken } from '../../../redux/actions/user/regenerateSessionToken';
import { AdminDashboardState } from '../../../redux/store';
import { logout } from '../../../utils/logout';
import { getInstitutionShortName } from '../../../selectors/institution';
import { getCurrentUserEmail } from '../../../selectors/user';
import { createCookie, getCookie } from '../../../utils/cookie';
import ActiveDialogBox from './ActiveDailogBox';

const AUTO_HIDE_DURATION = 30 * 1000;
const IDLE_SESSION_DURATION = 30 * 60 * 1000;
const REGENERATE_SESSION_DURATION = 25 * 60 * 1000;
const DEBOUNCE_TIME = 500;

interface StateProps {
  email: string;
  institution: string;
}

interface DispatchProps {
  regenerateSessionToken: () => void;
}

const mapStateToProps = (state: AdminDashboardState): StateProps => ({
  email: getCurrentUserEmail(state),
  institution: getInstitutionShortName(state),
});

const mapDispatchToProps: DispatchProps = {
  regenerateSessionToken,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type SessionManagerProps = ConnectedProps<typeof connector>;

const SessionManager: React.FC<SessionManagerProps> = ({
  regenerateSessionToken,
  email,
  institution,
}) => {
  const [isIdle, setIsIdle] = useState<boolean>(false);
  const { ftSessionTokenRefreshEnabled202412 } = useFlags();

  const LATEST_ACTIVITY_COOKIE = `${institution}-latest-activity-timestamp`;
  const SESSION_STATUS_COOKIE = `${institution}-session-status`;
  const setSessionStatusCookie = (value) => {
    createCookie(
      SESSION_STATUS_COOKIE,
      JSON.stringify(value),
      null,
      '/',
      'aula.education',
    );
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (ftSessionTokenRefreshEnabled202412) {
      setSessionStatusCookie({ sessionStatus: true, loggedOut: false });
      createCookie(
        LATEST_ACTIVITY_COOKIE,
        new Date().getTime(),
        null,
        '/',
        'aula.education',
      );
      const regenerateToken = () => {
        if (!isIdle) {
          regenerateSessionToken();
        }
      };

      // Set up interval for token regeneration
      const interval = setInterval(
        regenerateToken,
        REGENERATE_SESSION_DURATION,
      );

      // Cleanup function
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenerateSessionToken, ftSessionTokenRefreshEnabled202412]);

  const handleLogout = async () => {
    await logout({ email, institution, returnAfterLogin: true });
    setIsIdle(false);
    setSessionStatusCookie({ sessionStatus: false, loggedOut: true });
  };

  const handleOnIdle = () => {
    let value = false;
    const sessionStatus = JSON.parse(
      unescape(getCookie(SESSION_STATUS_COOKIE)),
    );
    // if session has expired on any other tab
    if (!sessionStatus.sessionStatus) {
      setIsIdle(true);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      if (sessionStatus.loggedOut) handleLogout();
      value = true;
    } else {
      const isValidSession =
        new Date().getTime() - Number(getCookie(LATEST_ACTIVITY_COOKIE)) <
        IDLE_SESSION_DURATION;
      if (!isValidSession) {
        setIsIdle(true);
        setSessionStatusCookie({ sessionStatus: false, loggedOut: false });
        value = true;
      } else {
        setIsIdle(false);
      }
    }
    return value;
  };

  useIdleTimer({
    onAction: () =>
      createCookie(
        LATEST_ACTIVITY_COOKIE,
        new Date().getTime(),
        null,
        '/',
        'aula.education',
      ),
    debounce: DEBOUNCE_TIME,
  });

  useEffect(() => {
    const interval = setInterval(async () => {
      if (ftSessionTokenRefreshEnabled202412) {
        await handleOnIdle();
      } else {
        clearInterval(interval);
      }
    }, AUTO_HIDE_DURATION);
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ftSessionTokenRefreshEnabled202412]);

  useEffect(() => {
    const HEARTBEAT_INTERVAL = 5000;
    const SLEEP_DRIFT_THRESHOLD = 2 * HEARTBEAT_INTERVAL;

    let lastTick = Date.now();

    const runDriftCheck = async (runAnyhow = false) => {
      const now = Date.now();
      const drift = now - lastTick;
      lastTick = now;

      if (drift > SLEEP_DRIFT_THRESHOLD || runAnyhow) {
        const idleStatus = handleOnIdle();
        if (idleStatus === false) {
          try {
            await regenerateSessionToken();
          } catch (error) {
            setIsIdle(true);
            setSessionStatusCookie({ sessionStatus: false, loggedOut: false });
          }
        }
      }
    };

    const runDriftWithTrue = () => {
      runDriftCheck(true);
    };

    if (ftSessionTokenRefreshEnabled202412) {
      const interval = setInterval(runDriftCheck, HEARTBEAT_INTERVAL);
      document.addEventListener('visibilitychange', () => runDriftCheck());
      window.addEventListener('online', runDriftWithTrue);

      return () => {
        document.removeEventListener('visibilitychange', () => runDriftCheck());
        window.removeEventListener('online', runDriftWithTrue);
        clearInterval(interval);
      };
    }

    return () => {};
  }, [ftSessionTokenRefreshEnabled202412]);

  const handleRegenerateSessionToken = async () => {
    await regenerateSessionToken();
    setSessionStatusCookie({ sessionStatus: true, loggedOut: false });
    setIsIdle(false);
  };

  return (
    <>
      {ftSessionTokenRefreshEnabled202412 && (
        <ActiveDialogBox
          open={isIdle}
          handleRegenerateSessionToken={handleRegenerateSessionToken}
          handleSessionLogout={handleLogout}
        />
      )}
    </>
  );
};

export default connector(SessionManager);
